import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Checkbox } from '@pwc/appkit-react/lib/Checkbox';

import Button from '../../shared/button.component';
import FormControl from '../../shared/forms/formControl.component';
import FormHelpers from '../../shared/forms/formHelpers';
import Select from '../../shared/reactSelect/select.component';
import Input from '../../shared/forms/input/input.component';
import NavigationPrompt from '../../shared/navigationPrompt.component';

import {
  CustomGroupPropTypes,
} from '../../shared/propTypes/customGroups';
import getInitialState from './selectedCustomGroupDetails.initialState';
import { CUSTOM_GROUP_TYPE_OPTIONS } from './constants';

import {
  fetchCustomGroups,
  editCustomGroup,
  saveNewCustomGroup,
  enterEditMode,
  exitEditMode,
  selectCustomGroupItemType,
  refreshCustomGroupData,
} from './store/actions';

import {
  customGroupItemTypesSelector,
  customGroupItemsSelector,
  selectedCustomGroupItemTypeSelector,
  isRefreshingCustomGroupsSelector,
  selectedCustomGroupIdSelector,
} from './store/selectors';
import { OptionsForAllCustomGroupTypes } from './propTypes';

class SelectedCustomGroupDetails extends PureComponent {
  static propTypes = {
    selectedCustomGroup: CustomGroupPropTypes,
    customGroupItemTypes: OptionsForAllCustomGroupTypes.isRequired,
    customGroupItems: OptionsForAllCustomGroupTypes.isRequired,
    selectedCustomGroupItemType: PropTypes.string,
    readOnly: PropTypes.bool.isRequired,
    editable: PropTypes.bool.isRequired,
    isCreatedGroup: PropTypes.bool.isRequired,
    customGroupItemsLoading: PropTypes.bool.isRequired,
    isArchiveAdmin: PropTypes.bool.isRequired,

    fetchCustomGroups: PropTypes.func.isRequired,
    saveNewCustomGroup: PropTypes.func.isRequired,
    editCustomGroup: PropTypes.func.isRequired,
    enterEditMode: PropTypes.func.isRequired,
    exitEditMode: PropTypes.func.isRequired,
    selectCustomGroupItemType: PropTypes.func.isRequired,
    refreshCustomGroupData: PropTypes.func.isRequired,
    onFormDirtyChange: PropTypes.func.isRequired,
    isRefreshingCustomGroups: PropTypes.bool.isRequired,
    selectedCustomGroupId: PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...getInitialState(props.selectedCustomGroup),
      selectedCustomGroupItemType: '',
    };
  }

  componentDidUpdate(oldProps, oldState) {
    if (oldProps.selectedCustomGroup !== this.props.selectedCustomGroup) {
      this.setState({
        ...getInitialState(
          this.props.selectedCustomGroup
        ),
        selectedCustomGroupItemType: '',
        shouldResetValues: true,
      }, () => {
        this.validateAll();
        this.setState({
          shouldResetValues: false,
        });
        this.selectCustomGroupItemType(null);
      });
    }

    if (!oldState.isFormDirty !== this.state.isFormDirty) {
      this.props.onFormDirtyChange(this.state.isFormDirty);
    }
  }

  selectCustomGroupItemType = itemType => {
    const value = itemType ? itemType.value : null;
    this.props.selectCustomGroupItemType(value);
  };

  changeProperty = event => {
    FormHelpers.changeProperty({
      event,
      setState: this.setState.bind(this),
    });
  };

  changePublicPrivate = event => {
    const mock = {
      target: {
        type: 'checkbox',
        checked: !event.target.checked,
        name: event.target.name,
      },
    };

    FormHelpers.changeProperty({
      event: mock,
      setState: this.setState.bind(this),
    });
  };

  changeArchival = event => {
    const mock = {
      target: {
        type: 'checkbox',
        checked: event.target.checked,
        name: event.target.name,
      },
    };

    FormHelpers.changeProperty({
      event: mock,
      setState: this.setState.bind(this),
    });
  };

  changeSftpDelivery = event => {
    const mock = {
      target: {
        type: 'checkbox',
        checked: event.target.checked,
        name: event.target.name,
      },
    };

    FormHelpers.changeProperty({
      event: mock,
      setState: this.setState.bind(this),
    });
  };

  validate = (key, { isValid }) => {
    FormHelpers.validate({
      key,
      isValid,
      setState: this.setState.bind(this),
      callback: () => {
        this.validateAll();
      },
    });
  };

  validateAll = () => {
    FormHelpers.validateAll(this.setState.bind(this));
  };

  validateName = value => {
    const regex = /[^0-9a-zA-Z ]+/;
    if (regex.test(value)) {
      return 'Custom Group Name can only contain alphanumeric characters and spaces.';
    }
  };

  saveCustomGroup = async () => {
    const { formData, isFormValid } = this.state;
    const {
      fetchCustomGroups,
      saveNewCustomGroup,
      editCustomGroup,
      exitEditMode,
      isCreatedGroup,
    } = this.props;

    const group = {
      name: formData.name.value,
      groupType: formData.groupType.value?.value,
      publicFlag: formData.publicFlag.value,
      archivalFlag: formData.archivalFlag.value,
      sftpDeliveryFlag: formData.sftpDeliveryFlag.value,
      allowEditFlag: formData.allowEditFlag.value,
      groupItem: formData.groupItem.value.map(item => ({ itemName: item.value, itemType: item.type })),
      id: this.props.selectedCustomGroup.id,
    };

    if (!isFormValid) {
      this.setState({
        triedToSubmitForm: true,
      });

      return;
    }

    exitEditMode();

    if (isCreatedGroup) {
      await saveNewCustomGroup(group);
    } else {
      await editCustomGroup(group);
    }

    fetchCustomGroups();
  };

  cancelEditing = () => {
    const { selectedCustomGroup, exitEditMode } = this.props;

    this.setState({
      ...getInitialState(
        selectedCustomGroup
      ),
      selectedCustomGroupItemType: '',
    });
    exitEditMode();
  };

  changeGroupItems = newItems => {
    const { formData } = this.state;

    this.setState({
      formData: {
        ...formData,
        groupItem: {
          value: newItems,
        },
      },
    });
  };

  changeGroupType = newType => {
    const { formData } = this.state;

    this.setState({
      formData: {
        ...formData,
        groupType: {
          value: newType,
        },
        archivalFlag: {
          ...formData.archivalFlag,
          value: newType?.value !== 'BR' ? false : formData.archivalFlag.value,
        },
        sftpDeliveryFlag: {
          ...formData.sftpDeliveryFlag,
          value: newType?.value !== 'BR' ? false : formData.sftpDeliveryFlag.value,
        },
      },
    });
  };

  enterEditMode = () => {
    const {
      enterEditMode,
      selectedCustomGroup,
    } = this.props;

    enterEditMode(selectedCustomGroup.id);
  };

  refreshCustomGroupData = () => this.props.refreshCustomGroupData();

  render() {
    const {
      formData,
      triedToSubmitForm,
      shouldResetValues,
      isFormDirty,
    } = this.state;

    const {
      readOnly,
      editable,
      customGroupItems,
      customGroupItemsLoading,
      isArchiveAdmin,
      selectedCustomGroupItemType,
      customGroupItemTypes,
      isRefreshingCustomGroups,
      selectedCustomGroupId,
    } = this.props;

    const isEditMode = !readOnly && selectedCustomGroupId;
    const groupType = formData.groupType?.value?.value;

    return (
      <div>
        <NavigationPrompt show={isFormDirty}/>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <FormControl
              onChange={this.changeProperty}
              name="name"
              label="Custom Group Name"
              value={formData.name.value}
              validate={this.validate}
              customValidation={this.validateName}
              showErrorByForce={triedToSubmitForm}
              shouldReset={shouldResetValues}
              horizontal
              required
            >
              <Input type="text" disabled={readOnly} maxLength="30"/>
            </FormControl>

            <FormControl
              onChange={this.changeGroupType}
              name="groupType"
              label="Custom Group Type"
              validate={this.validate}
              value={formData.groupType?.value}
              horizontal
              required
            >
              <Select
                disabled={readOnly || isEditMode}
                options={CUSTOM_GROUP_TYPE_OPTIONS}
              />
            </FormControl>

            <FormControl
              onChange={this.selectCustomGroupItemType}
              value={selectedCustomGroupItemType}
              name="itemType"
              label="Item Type"
              validate={this.validate}
              horizontal
            >
              <Select
                disabled={readOnly || !groupType}
                options={(groupType && customGroupItemTypes[groupType]) || []}
                clearable
              />
            </FormControl>
          </div>

          <div className="col-xs-12 col-md-6 group-checkbox">
            <FormControl
              onChange={this.changePublicPrivate}
              name="publicFlag"
              validate={this.validate}
              label="Make private"
              hideLabel
              horizontal
            >
              <Checkbox
                disabled={readOnly}
                checked={!formData.publicFlag.value}
              >
                Make private
              </Checkbox>
            </FormControl>
            <FormControl
              onChange={this.changeArchival}
              name="archivalFlag"
              validate={this.validate}
              label="Archival"
              hideLabel
              horizontal
            >
              <Checkbox
                disabled={readOnly || groupType !== 'BR' || !isArchiveAdmin}
                checked={formData.archivalFlag.value && groupType === 'BR'}
              >
                Archival
              </Checkbox>
            </FormControl>
            <FormControl
              onChange={this.changeSftpDelivery}
              name="sftpDeliveryFlag"
              validate={this.validate}
              label="SFTP Delivery"
              hideLabel
              horizontal
            >
              <Checkbox
                disabled={readOnly || groupType !== 'BR' || !isArchiveAdmin}
                checked={formData.sftpDeliveryFlag.value && groupType === 'BR'}
              >
                SFTP Delivery
              </Checkbox>
            </FormControl>
            {formData.publicFlag.value && (
              <FormControl
                onChange={this.changeProperty}
                name="allowEditFlag"
                validate={this.validate}
                label="Other users can modify custom group"
                hideLabel
                horizontal
              >
                <Checkbox
                  disabled={readOnly}
                  checked={formData.allowEditFlag.value}
                >
                  Other users can modify custom group
                </Checkbox>
              </FormControl>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-12 group-items">
            <FormControl
              onChange={this.changeGroupItems}
              name="items"
              label="Group Items"
              value={formData.groupItem.value}
              horizontal
            >
              <Select
                options={(groupType && customGroupItems[groupType]) || []}
                multi
                virtualized
                removeSelected
                closeOnSelect={false}
                disabled={customGroupItemsLoading || !groupType || readOnly}
              />
            </FormControl>
          </div>
        </div>

        {editable && (
          <div className="d-flex flex-row-reverse mt-2">
            {readOnly && (
              <Button
                type="button"
                kind="primary"
                onClick={this.refreshCustomGroupData}
                disabled={isRefreshingCustomGroups}
              >
                Refresh Custom Group Data
              </Button>)
            }
            {readOnly && (
              <Button
                type="button"
                kind="primary"
                className="a-mx-10"
                onClick={this.enterEditMode}
              >
                Edit Custom Group
              </Button>
            )}
            {!readOnly && (
              <Button
                type="button"
                kind="primary"
                onClick={this.saveCustomGroup}
              >
                Save
              </Button>
            )}
            {!readOnly && (
              <Button
                type="button"
                kind="secondary"
                className="a-mx-10"
                onClick={this.cancelEditing}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { customGroups } = state.administration;

  return {
    customGroups: customGroups.customGroups,
    customGroupsLoading: customGroups.customGroupsLoading,
    customGroupItemTypes: customGroupItemTypesSelector(state),
    customGroupItems: customGroupItemsSelector(state),
    selectedCustomGroupItemType: selectedCustomGroupItemTypeSelector(state),
    isRefreshingCustomGroups: isRefreshingCustomGroupsSelector(state),
    selectedCustomGroupId: selectedCustomGroupIdSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchCustomGroups,
    editCustomGroup,
    saveNewCustomGroup,
    enterEditMode,
    exitEditMode,
    selectCustomGroupItemType,
    refreshCustomGroupData,
  }
)(SelectedCustomGroupDetails);
